import {
  useState,
  useCallback,
  useRef,
  useEffect,
  useImperativeHandle,
  forwardRef,
} from 'react';
import { FormattedMessage, useIntl, defineMessages } from 'react-intl';
import classNames from 'classnames';
import type { List as ImmutableList, Map as ImmutableMap } from 'immutable';
import { length } from 'stringz';
import { undoUploadCompose, uploadCompose, uploadThumbnail } from 'mastodon/actions/compose';
import { Button } from 'mastodon/components/button';
import { GIFV } from 'mastodon/components/gifv';
import { LoadingIndicator } from 'mastodon/components/loading_indicator';
import Audio from 'mastodon/features/audio';
import Video, { getPointerPosition } from 'mastodon/features/video';
import { me } from 'mastodon/initial_state';
import type { MediaAttachment } from 'mastodon/models/media_attachment';
import { useAppSelector, useAppDispatch } from 'mastodon/store';
import ReactCrop, { centerCrop, convertToPixelCrop, Crop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/src/ReactCrop.scss';

const messages = defineMessages({
  placeholderVisual: {
    id: 'alt_text_modal.describe_for_people_with_visual_impairments',
    defaultMessage: 'Describe this for people with visual impairments…',
  },
  placeholderHearing: {
    id: 'alt_text_modal.describe_for_people_with_hearing_impairments',
    defaultMessage: 'Describe this for people with hearing impairments…',
  },
  discardMessage: {
    id: 'confirmations.discard_edit_media.message',
    defaultMessage:
      'You have unsaved changes to the media description or preview, discard them anyway?',
  },
  discardConfirm: {
    id: 'confirmations.discard_edit_media.confirm',
    defaultMessage: 'Discard',
  },
  selectAspectRatio: {
    id: 'alt_text_modal.select_aspect_ratio',
    defaultMessage: 'Select aspect ratio',
  },
});

const MAX_LENGTH = 1500;

const ASPECT_RATIOS = [
  { label: '1:1', value: 1 },
  { label: '3:2', value: 3 / 2 },
  { label: '16:9', value: 16 / 9 },
  { label: '4:3', value: 4 / 3 },
];

type FocalPoint = [number, number];

const UploadButton: React.FC<{
  children: React.ReactNode;
  onSelectFile: (arg0: File) => void;
  mimeTypes: string;
}> = ({ children, onSelectFile, mimeTypes }) => {
  const fileRef = useRef<HTMLInputElement>(null);

  const handleClick = useCallback(() => {
    fileRef.current?.click();
  }, []);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (file) {
        onSelectFile(file);
      }
    },
    [onSelectFile],
  );

  return (
    <label>
      <Button onClick={handleClick}>{children}</Button>
      <input
        id='upload-modal__thumbnail'
        ref={fileRef}
        type='file'
        accept={mimeTypes}
        onChange={handleChange}
        style={{ display: 'none' }}
      />
    </label>
  );
};

const Preview: React.FC<{
  mediaId: string;
  position: FocalPoint;
  setImg: (arg0: HTMLImageElement | null) => void;
  onPositionChange: (arg0: FocalPoint) => void;
  setCropValue: (arg0: Crop) => void;
  aspectRatio: number;
}> = ({ mediaId, position, onPositionChange, setImg, setCropValue, aspectRatio }) => {
  const media = useAppSelector((state) =>
    (
      (state.compose as ImmutableMap<string, unknown>).get(
        'media_attachments',
      ) as ImmutableList<MediaAttachment>
    ).find((x) => x.get('id') === mediaId),
  );
  const account = useAppSelector((state) =>
    me ? state.accounts.get(me) : undefined,
  );
  const [crop, setCrop] = useState<Crop>();
  const imageRef = useRef<HTMLImageElement | null>(null);

  const handleCropComplete = useCallback(
    (newCrop: Crop) => {
      if (!imageRef.current) return;
      const focalX = newCrop.x + newCrop.width / 2;
      const focalY = newCrop.y + newCrop.height / 2;
      onPositionChange([focalX, focalY]);
      setCropValue(newCrop);
      setImg(imageRef.current);
    },
    [onPositionChange, setCropValue, setImg],
  );

  const handleCropChange = useCallback(
    (_: Crop, percentCrop: Crop) => {
      setCrop(percentCrop);
    },
    [setCrop],
  );

  const updateCrop = useCallback(() => {
    if (!imageRef.current) return;
    const { width, height } = imageRef.current;
    let cropWidth, cropHeight;

    // Tính kích thước vùng crop dựa trên tỷ lệ và kích thước hình ảnh
    if (aspectRatio >= 1) {
      // Nếu tỷ lệ >= 1 (như 1:1, 3:2, 16:9, 4:3), lấy chiều cao làm cơ sở nếu nó nhỏ hơn
      if (width / height > aspectRatio) {
        cropHeight = height;
        cropWidth = height * aspectRatio;
      } else {
        cropWidth = width;
        cropHeight = width / aspectRatio;
      }
    } else {
      if (height / width > 1 / aspectRatio) {
        cropWidth = width;
        cropHeight = width / aspectRatio;
      } else {
        cropHeight = height;
        cropWidth = height * aspectRatio;
      }
    }

    const newCrop: Crop = {
      unit: 'px',
      x: (width - cropWidth) / 2,
      y: (height - cropHeight) / 2,
      width: cropWidth,
      height: cropHeight,
    };

    setCrop(newCrop);
    setCropValue(newCrop);
  }, [aspectRatio, setCrop, setCropValue]);

  useEffect(() => {
    if (imageRef.current) {
      updateCrop();
    }
  }, [aspectRatio, updateCrop]);

  const handleImageLoad = useCallback(
    (e: React.SyntheticEvent<HTMLImageElement>) => {
      imageRef.current = e.currentTarget;
      setImg(e.currentTarget);
      updateCrop();
    },
    [setImg, updateCrop],
  );

  const [dragging, setDragging] = useState(false);
  const [x, y] = position;
  const nodeRef = useRef<HTMLImageElement | HTMLVideoElement | null>(null);
  const draggingRef = useRef<boolean>(false);

  const setRef = useCallback(
    (e: HTMLImageElement | HTMLVideoElement | null) => {
      nodeRef.current = e;
    },
    [],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (e.button !== 0) return;
      const { x, y } = getPointerPosition(nodeRef.current, e);
      setDragging(true);
      draggingRef.current = true;
      onPositionChange([x, y]);
    },
    [setDragging, onPositionChange],
  );

  const handleTouchStart = useCallback(
    (e: React.TouchEvent) => {
      const { x, y } = getPointerPosition(nodeRef.current, e);
      setDragging(true);
      draggingRef.current = true;
      onPositionChange([x, y]);
    },
    [setDragging, onPositionChange],
  );

  useEffect(() => {
    const handleMouseUp = () => {
      setDragging(false);
      draggingRef.current = false;
    };

    const handleMouseMove = (e: MouseEvent) => {
      if (draggingRef.current) {
        const { x, y } = getPointerPosition(nodeRef.current, e);
        onPositionChange([x, y]);
      }
    };

    const handleTouchEnd = () => {
      setDragging(false);
      draggingRef.current = false;
    };

    const handleTouchMove = (e: TouchEvent) => {
      if (draggingRef.current) {
        const { x, y } = getPointerPosition(nodeRef.current, e);
        onPositionChange([x, y]);
      }
    };

    document.addEventListener('mouseup', handleMouseUp);
    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('touchend', handleTouchEnd);
    document.addEventListener('touchmove', handleTouchMove);

    return () => {
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('touchend', handleTouchEnd);
      document.removeEventListener('touchmove', handleTouchMove);
    };
  }, [setDragging, onPositionChange]);

  if (!media) {
    return null;
  }

  if (media.get('type') === 'image') {
    return (
      <div className={classNames('focal-point')}>
        <ReactCrop
          crop={crop}
          onChange={handleCropChange}
          onComplete={handleCropComplete}
          aspect={aspectRatio}
          keepSelection
        >
          <img
            ref={imageRef}
            src={media.get('url') as string}
            crossOrigin='anonymous'
            alt='Crop preview'
            style={{ maxHeight: '70vh' }}
            onLoad={handleImageLoad}
          />
        </ReactCrop>
      </div>
    );
  } else if (media.get('type') === 'gifv') {
    return (
      <div className={classNames('focal-point', { dragging })}>
        <GIFV
          ref={setRef}
          src={media.get('url') as string}
          alt=''
          onMouseDown={handleMouseDown}
          onTouchStart={handleTouchStart}
        />
        <div
          className='focal-point__reticle'
          style={{ top: `${y * 100}%`, left: `${x * 100}%` }}
        />
      </div>
    );
  } else if (media.get('type') === 'video') {
    return (
      <Video
        preview={media.get('preview_url') as string}
        frameRate={media.getIn(['meta', 'original', 'frame_rate']) as string}
        blurhash={media.get('blurhash') as string}
        src={media.get('url') as string}
        detailed
        inline
        editable
      />
    );
  } else if (media.get('type') === 'audio') {
    return (
      <Audio
        src={media.get('url') as string}
        duration={media.getIn(['meta', 'original', 'duration'], 0) as number}
        poster={
          (media.get('preview_url') as string | undefined) ??
          account?.avatar_static
        }
        backgroundColor={
          media.getIn(['meta', 'colors', 'background']) as string
        }
        foregroundColor={
          media.getIn(['meta', 'colors', 'foreground']) as string
        }
        accentColor={media.getIn(['meta', 'colors', 'accent']) as string}
        editable
      />
    );
  } else {
    return null;
  }
};

interface RestoreProps {
  previousDescription: string;
  previousPosition: FocalPoint;
}

interface Props {
  mediaId: string;
  onClose: () => void;
}

interface ConfirmationMessage {
  message: string;
  confirm: string;
  props?: RestoreProps;
}

export interface ModalRef {
  getCloseConfirmationMessage: () => null | ConfirmationMessage;
}

export const ImageEditModal = forwardRef<ModalRef, Props & Partial<RestoreProps>>(
  ({ mediaId, previousDescription, previousPosition, onClose }, ref) => {
    const intl = useIntl();
    const dispatch = useAppDispatch();
    const media = useAppSelector((state) =>
      (
        (state.compose as ImmutableMap<string, unknown>).get(
          'media_attachments',
        ) as ImmutableList<MediaAttachment>
      ).find((x) => x.get('id') === mediaId),
    );
    const focusX =
      (media?.getIn(['meta', 'focus', 'x'], 0) as number | undefined) ?? 0;
    const focusY =
      (media?.getIn(['meta', 'focus', 'y'], 0) as number | undefined) ?? 0;

    const [description, _setDescription] = useState(
      previousDescription ??
      (media?.get('description') as string | undefined) ??
      '',
    );
    const [position, setPosition] = useState<FocalPoint>(
      previousPosition ?? [focusX / 2 + 0.5, focusY / -2 + 0.5],
    );
    const [img, setImg] = useState<HTMLImageElement | null>(null);
    const [isSaving, setIsSaving] = useState(false);
    const [cropValue, setCropValue] = useState<Crop>();
    const [aspectRatio, setAspectRatio] = useState<number>(3 / 2); // Tỷ lệ mặc định
    const dirtyRef = useRef(
      previousDescription || previousPosition ? true : false,
    );
    const type = media?.get('type') as string;
    const valid = length(description) <= MAX_LENGTH;

    const handleThumbnailChange = useCallback(
      (file: File) => {
        dispatch(uploadThumbnail(mediaId, file));
      },
      [dispatch, mediaId],
    );

    const handlePositionChange = useCallback(
      (position: FocalPoint) => {
        setPosition(position);
        dirtyRef.current = true;
      },
      [setPosition],
    );

    const handleAspectRatioChange = useCallback(
      (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newAspectRatio = parseFloat(e.target.value);
        setAspectRatio(newAspectRatio);
      },
      [],
    );

    const generateCroppedImage = useCallback(
      (image: HTMLImageElement, crop: Crop): void => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        if (!ctx) {
          throw new Error('No 2D context available on canvas');
        }

        const scaleX = image.naturalWidth / image.width;
        const scaleY = image.naturalHeight / image.height;

        canvas.width = Math.floor(crop.width * scaleX);
        canvas.height = Math.floor(crop.height * scaleY);

        ctx.imageSmoothingQuality = 'high';
        ctx.save();

        const cropX = crop.x * scaleX;
        const cropY = crop.y * scaleY;

        ctx.translate(-cropX, -cropY);
        ctx.drawImage(
          image,
          0,
          0,
          image.naturalWidth,
          image.naturalHeight,
          0,
          0,
          image.naturalWidth,
          image.naturalHeight,
        );
        ctx.restore();

        canvas.toBlob(
          (blob) => {
            if (!blob) return;
            const file = new File([blob], 'cropped_image.jpg', {
              type: 'image/jpeg',
              lastModified: Date.now(),
            });
            dispatch(uploadCompose([file]));
          },
          'image/jpeg',
          1,
        );
      },
      [dispatch],
    );

    const handleSubmit = useCallback(async () => {
      setIsSaving(true);
      if (cropValue && img) {
        await generateCroppedImage(img, convertToPixelCrop(cropValue, img.width, img.height));
        dispatch(undoUploadCompose(mediaId));
        setIsSaving(false);
        dirtyRef.current = false;
        onClose();
      }
    }, [dispatch, setIsSaving, mediaId, onClose, cropValue, img]);
    
    useImperativeHandle(
      ref,
      () => ({
        getCloseConfirmationMessage: () => {
          if (dirtyRef.current) {
            return {
              message: intl.formatMessage(messages.discardMessage),
              confirm: intl.formatMessage(messages.discardConfirm),
              props: {
                previousDescription: description,
                previousPosition: position,
              },
            };
          }
          return null;
        },
      }),
      [intl, description, position],
    );

    return (
      <div className='modal-root__modal dialog-modal'>
        <div className='dialog-modal__header'>
          <Button onClick={handleSubmit} disabled={!valid}>
            {isSaving ? (
              <LoadingIndicator />
            ) : (
              <FormattedMessage id='alt_text_modal.done' defaultMessage='Done' />
            )}
          </Button>

          <span className='dialog-modal__header__title'>
            <FormattedMessage
              id='alt_text_modal.crop_image'
              defaultMessage='Crop image'
            />
          </span>

          <Button secondary onClick={onClose}>
            <FormattedMessage
              id='alt_text_modal.cancel'
              defaultMessage='Cancel'
            />
          </Button>
        </div>

        <div className='dialog-modal__content'>
          <div className='dialog-modal__content__preview'>
            {type === 'image' && (
              <div className='aspect-ratio-selector' style={{ marginBottom: '10px' }}>
                <label htmlFor='aspect-ratio-select'>
                  <FormattedMessage
                    id='alt_text_modal.select_aspect_ratio'
                    defaultMessage='Select aspect ratio'
                  />
                </label>
                <select
                  id='aspect-ratio-select'
                  value={aspectRatio}
                  onChange={handleAspectRatioChange}
                >
                  {ASPECT_RATIOS.map((ratio) => (
                    <option key={ratio.value} value={ratio.value}>
                      {ratio.label}
                    </option>
                  ))}
                </select>
              </div>
            )}
            <Preview
              mediaId={mediaId}
              position={position}
              onPositionChange={handlePositionChange}
              setImg={setImg}
              setCropValue={setCropValue}
              aspectRatio={aspectRatio}
            />

            {(type === 'audio' || type === 'video') && (
              <UploadButton
                onSelectFile={handleThumbnailChange}
                mimeTypes='image/jpeg,image/png,image/gif,image/heic,image/heif,image/webp,image/avif'
              >
                <FormattedMessage
                  id='alt_text_modal.change_thumbnail'
                  defaultMessage='Change thumbnail'
                />
              </UploadButton>
            )}
          </div>
        </div>
      </div>
    );
  },
);

ImageEditModal.displayName = 'ImageEditModal';